'use client';

import { cn } from '@heroui/react';
import { motion } from 'motion/react';
import { type ComponentPropsWithoutRef, useCallback, useEffect, useId, useRef, useState } from 'react';

export interface AnimatedGridPatternProps extends ComponentPropsWithoutRef<'svg'> {
	width?: number;
	height?: number;
	x?: number;
	y?: number;
	strokeDasharray?: number;
	numSquares?: number;
	maxOpacity?: number;
	duration?: number;
}

export default function AnimatedGridPattern({
	width = 40,
	height = 40,
	x = -1,
	y = -1,
	strokeDasharray = 0,
	numSquares = 50,
	className,
	maxOpacity = 0.5,
	duration = 4,
	...props
}: AnimatedGridPatternProps) {
	const id = useId();
	const containerRef = useRef(null);
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

	const getPos = useCallback(() => {
		return [Math.floor((Math.random() * dimensions.width) / width), Math.floor((Math.random() * dimensions.height) / height)];
	}, [dimensions, height, width]);

	const generateSquares = useCallback(
		(count: number) => {
			return Array.from({ length: count }, (_, i) => ({
				id: i,
				pos: getPos(),
			}));
		},
		[getPos],
	);

	const [squares, setSquares] = useState(() => generateSquares(numSquares));

	const updateSquarePosition = (id: number) => {
		setSquares((currentSquares) =>
			currentSquares.map((sq) =>
				sq.id === id
					? {
							...sq,
							pos: getPos(),
						}
					: sq,
			),
		);
	};

	useEffect(() => {
		if (dimensions.width && dimensions.height) {
			setSquares(generateSquares(numSquares));
		}
	}, [dimensions, numSquares, generateSquares]);

	useEffect(() => {
		const current_ref = containerRef.current;
		const resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setDimensions({
					width: entry.contentRect.width,
					height: entry.contentRect.height,
				});
			}
		});

		if (current_ref) {
			resizeObserver.observe(current_ref);
		}

		return () => {
			if (current_ref) {
				resizeObserver.unobserve(current_ref);
			}
		};
	}, []);

	return (
		<svg
			ref={containerRef}
			aria-hidden="true"
			className={cn('fill-default-400/30 stroke-default-400/30 pointer-events-none fixed inset-0 size-full', className)}
			{...props}
		>
			<defs>
				<pattern id={id} width={width} height={height} patternUnits="userSpaceOnUse" x={x} y={y}>
					<path d={`M.5 ${height}V.5H${width}`} fill="none" strokeDasharray={strokeDasharray} />
				</pattern>
			</defs>
			<rect width="100%" height="100%" fill={`url(#${id})`} />
			<svg x={x} y={y} className="overflow-visible">
				{squares.map(({ pos: [x, y], id }, index) => (
					<motion.rect
						initial={{ opacity: 0 }}
						animate={{ opacity: maxOpacity }}
						transition={{
							duration,
							repeat: 1,
							delay: index * 0.1,
							repeatType: 'reverse',
						}}
						onAnimationComplete={() => updateSquarePosition(id)}
						key={`${x}-${y}-${index}`}
						width={width - 1}
						height={height - 1}
						x={x * width + 1}
						y={y * height + 1}
						fill="currentColor"
						strokeWidth="0"
					/>
				))}
			</svg>
		</svg>
	);
}
